import { useEffect, useMemo, useState } from 'react';
import {
  cibleRelance, composerRelanceFournisseur, expedieLuiMeme, LIBELLE_LANGUE,
  motifRelance, REGLAGES_PAR_DEFAUT,
} from '@mosaik/core';
import { ErreurApi } from '../api/client';
import { useRelance } from '../api/hooks';
import type { CommandeListee, DocumentReglages } from '../api/types';
import { RelanceHorsMail } from './RelanceHorsMail';
import { useToasts } from './Toasts';
import { Repousser } from './Repousser';
import { RelancesEnvoyees } from './RelancesEnvoyees';

/**
 * Fenêtre de relance fournisseur.
 *
 * Le mail arrive écrit : destinataires, langue et texte viennent de l'annuaire
 * et des modèles de Réglages, remplis avec les données de la commande. Tout
 * reste modifiable avant l'envoi — c'est ce texte-là, corrections comprises,
 * qui part chez le fournisseur, l'API ne rejoue pas le modèle.
 *
 * Le mail part depuis contact@ au clic sur « Envoyer », comme le mail de
 * tracking : cette fenêtre est la seule relecture, il n'y a plus d'étape Outlook.
 */
export function RelanceFournisseur({
  commande, reglages, onFermer,
}: {
  commande: CommandeListee;
  reglages: DocumentReglages | undefined;
  onFermer: () => void;
}) {
  const { ajouter } = useToasts();
  const relance = useRelance();

  // Une commande peut porter plusieurs fournisseurs : on relance l'un après
  // l'autre, jamais les deux dans le même mail.
  const [societe, setSociete] = useState(commande.fournisseurs[0] ?? '');

  useEffect(() => { setSociete(commande.fournisseurs[0] ?? ''); }, [commande.id]);

  /*
   * Ce qu'on réclame — calculé UNE fois, pour le texte comme pour le journal.
   *
   * Le composer d'un côté et l'inscrire de l'autre laisserait deux réponses à
   * une seule question : le mail pourrait demander un numéro de suivi pendant
   * que le journal consigne une relance de livraison.
   */
  const motif = useMemo(
    () => motifRelance(
      commande,
      expedieLuiMeme(reglages ?? REGLAGES_PAR_DEFAUT, commande.fournisseurs),
    ),
    [commande, reglages],
  );

  const modele = useMemo(() => {
    // Les lignes du fournisseur visé seulement : lui rappeler les articles d'un
    // confrère n'aiderait personne à retrouver la commande.
    const articles = commande.articles.filter(
      (a) => !societe || !a.fournisseur || a.fournisseur === societe,
    );
    return composerRelanceFournisseur(reglages ?? REGLAGES_PAR_DEFAUT, {
      ref: commande.ref,
      societe,
      client: commande.client,
      ville: commande.ville,
      dateReception: commande.dateReception,
      dateEnvoiFournisseur: commande.dateEnvoiFournisseur,
      note: commande.note,
      articles: articles.map((a) => ({
        reference: a.reference,
        correspondance: a.correspondance,
        nom: a.nom,
        quantite: a.quantite,
        unite: a.unite,
      })),
      aujourdhui: new Date().toISOString().slice(0, 10),
      // Inutilisés par les modèles par défaut — une relance ne récite pas la
      // commande — mais toujours fournis : `{{articles}}` et `{{adresse}}`
      // restent des variables valides pour un modèle modifié depuis Réglages.
      adresse: commande.adresse,
      entreprise: commande.entreprise,
      telephone: commande.telephone,
      // Ne sert qu'au modèle « numéro de suivi », qui date le départ.
      dateExpedition: commande.dateExpedition,
    }, motif);
  }, [commande, reglages, societe, motif]);

  const [to, setTo] = useState(modele.destinataires);
  const [objet, setObjet] = useState(modele.objet);
  const [corps, setCorps] = useState(modele.corps);
  const [erreur, setErreur] = useState<string | null>(null);

  // Changer de fournisseur recompose tout : adresses, langue et texte en
  // dépendent. Les corrections faites sur le mail précédent n'auraient aucun
  // sens pour le suivant.
  useEffect(() => {
    setTo(modele.destinataires); setObjet(modele.objet); setCorps(modele.corps);
  }, [modele]);

  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  /**
   * Envoie la relance.
   *
   * Le mail d'abord, le statut ensuite : la commande ne passe en « relancé »
   * que si le mail est parti. Un échec laisse la fenêtre ouverte,
   * texte intact — la relance est à refaire, pas à réécrire.
   */
  async function envoyer() {
    setErreur(null);
    try {
      const r = await relance.mutateAsync({
        id: commande.id,
        corps: {
          /*
           * La cible dit à QUI l'on a écrit, le motif CE QU'ON réclamait.
           *
           * Cette fenêtre n'est plus réservée aux fournisseurs : elle sert à
           * toute réclamation d'une date d'expédition, y compris auprès d'une
           * société qui est elle-même le transport. Inscrire « fournisseur »
           * d'office aurait fait mentir le journal des relances — et c'est
           * justement cette confusion entre les deux questions qui composait
           * naguère un mail de livraison bloquée sur une commande jamais partie.
           */
          cible: cibleRelance(reglages ?? REGLAGES_PAR_DEFAUT, commande.fournisseurs),
          motif,
          fournisseur: societe || undefined,
          destinataire: to,
          objet,
          message: corps,
        },
      });

      if (!r.brouillon.depose) {
        setErreur(
          `Relance non enregistrée : ${r.brouillon.erreur ?? "le mail n'a pas pu être envoyé."}`,
        );
        return;
      }

      ajouter(
        r.brouillon.simule
          ? "Relance enregistrée, mais Outlook est en mode local : aucun mail envoyé."
          : `${societe || 'Fournisseur'} relancé — mail envoyé depuis contact@.`,
        r.brouillon.simule ? 'bad' : 'ok',
      );
      onFermer();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "L'enregistrement a échoué.");
    }
  }

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div className="box" role="dialog" aria-modal="true" aria-label={`Relance fournisseur ${commande.ref}`}>
        <div className="mhead">
          <h2>Relancer {societe || 'le fournisseur'} — {commande.ref}</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>

        <div className="mbody">
          <RelancesEnvoyees
            commande={commande} cible={cibleRelance(reglages ?? REGLAGES_PAR_DEFAUT, commande.fournisseurs)}
          />
          {commande.fournisseurs.length > 1 && (
            <div className="field">
              <label htmlFor="rf-frs">Fournisseur à relancer</label>
              <select id="rf-frs" value={societe} onChange={(e) => setSociete(e.target.value)}>
                {commande.fournisseurs.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <p className="indice">
                Cette commande en compte {commande.fournisseurs.length}. Un mail par fournisseur.
              </p>
            </div>
          )}

          <div className="field">
            <label htmlFor="rf-to">
              Destinataires
              <span className="tag" style={{ marginLeft: 8 }}>{LIBELLE_LANGUE[modele.langue]}</span>
            </label>
            <input
              id="rf-to" value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="adresses séparées par des virgules"
            />
            {modele.contactConnu ? (
              <p className="indice">
                Adresses et langue reprises de l&apos;annuaire fournisseurs (Réglages).
              </p>
            ) : (
              <p className="err">
                {societe || 'Ce fournisseur'} ne figure pas à l&apos;annuaire : saisissez les adresses
                ici, et ajoutez-les dans Réglages pour la prochaine fois. Le mail est écrit en
                français par défaut.
              </p>
            )}
          </div>

          <div className="field">
            <label htmlFor="rf-obj">Objet</label>
            <input id="rf-obj" value={objet} onChange={(e) => setObjet(e.target.value)} />
          </div>

          <div className="field">
            <label htmlFor="rf-corps">Message</label>
            <textarea id="rf-corps" rows={16} value={corps} onChange={(e) => setCorps(e.target.value)} />
          </div>

          {erreur && <p className="err" role="alert">{erreur}</p>}

          <p style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
            Modèle et annuaire modifiables dans Réglages. Le mail{' '}
            <strong>part dès que vous cliquez sur Envoyer</strong>, depuis contact@ : relisez-le
            ici, il n&apos;y a pas d&apos;autre relecture. La commande n&apos;est marquée relancée
            que si le mail est bien parti.
            {commande.relancesFournisseur > 0 && (
              <> Déjà relancée {commande.relancesFournisseur} fois.</>
            )}
          </p>
        </div>

        <div className="mfoot">
          <Repousser commande={commande} serre />
          {/* Le téléphone a parfois devancé le mail : on le consigne ici, sans
              mail, plutôt que d'envoyer un mail devenu inutile. */}
          <RelanceHorsMail
            commande={commande}
            cible={cibleRelance(reglages ?? REGLAGES_PAR_DEFAUT, commande.fournisseurs)}
            motif={motif} fournisseur={societe || null} onFait={onFermer}
          />
          <button
            type="button" className="btn pri" disabled={relance.isPending}
            onClick={() => void envoyer()}
          >
            {relance.isPending
              ? <><span className="spin" /> Envoi…</>
              : 'Envoyer la relance'}
          </button>
        </div>
      </div>
    </div>
  );
}
